/**
 * What a demo booking means, shared by the bookings screen, the follow-up
 * list and the client record.
 *
 * ## Times are Dubai's
 *
 * The demos are run from Dubai and Calendly reports them in UTC. Showing
 * them in whatever timezone the laptop happens to be set to made a 3pm
 * meeting read as 12:30pm to somebody working from India, which is the one
 * mistake a booking screen must not make. Every time on these screens is
 * therefore formatted in Asia/Dubai, and the screens say so.
 *
 * ## Except the follow-up journey, which is the team's own clock
 *
 * A booking time is an appointment two people have to keep, and the one
 * that matters is the host's. A follow-up round is not an appointment: it
 * is a record of when somebody on this team picked up the phone, read by
 * that same team in India. "I called at 3:53" should read back as 3:53, so
 * the journey is stamped in Asia/Kolkata and says so.
 */

export const DAY = 86400000;
export const TZ = 'Asia/Dubai';
export const TZ_LABEL = 'Dubai time';

/* Where the follow-up work is done, as opposed to where the demos are run. */
export const TZ_WORK = 'Asia/Kolkata';
export const TZ_WORK_LABEL = 'India time';

const fmt = (opts, tz = TZ) => new Intl.DateTimeFormat('en-GB', { timeZone: tz, ...opts });

/*
 * A date, or nothing.
 *
 * `new Date('')` and `new Date(null)` are not errors: the first is an
 * Invalid Date and the second is midnight on 1 January 1970. Both used to
 * go straight to Intl, so a client with no last payment exported as
 * "01 Jan 1970, 04:00 am" — a real-looking date, in 33 rows of the demo
 * report, for a payment that never happened. A blank is the answer; a
 * plausible wrong date is worse than an obvious gap.
 */
const when = d => {
  if (d === null || d === undefined || d === '') return null;
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? null : t;
};

/** "03:45 pm" in Dubai, or in whichever zone is asked for. */
export const hhmm = (d, tz = TZ) => {
  const t = when(d);
  return t ? fmt({ hour: '2-digit', minute: '2-digit', hour12: true }, tz).format(t).toLowerCase() : '';
};

/** "16 Sep 2026" in Dubai, or in whichever zone is asked for. */
export const dayText = (d, tz = TZ) => {
  const t = when(d);
  return t ? fmt({ day: '2-digit', month: 'short', year: 'numeric' }, tz).format(t) : '';
};

/** "Mon" in Dubai. */
export const weekday = d => {
  const t = when(d);
  return t ? fmt({ weekday: 'long' }).format(t) : '';
};

/** "16 Sep 2026, 03:45 pm" in Dubai, or '' where there is no date. */
export const stamp = d => (when(d) ? `${dayText(d)}, ${hhmm(d)}` : '');

/** The same, on the clock the follow-up team is actually working to. */
export const workStamp = d => (when(d) ? `${dayText(d, TZ_WORK)}, ${hhmm(d, TZ_WORK)}` : '');

/**
 * Midnight in Dubai on the day the given instant falls, as epoch ms.
 *
 * Grouping by day has to be done in Dubai as well, or a 1am booking lands
 * under the wrong heading for anybody east of it.
 */
export function dubaiDayStart(d) {
  const parts = fmt({ year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(d));
  const get = t => parts.find(p => p.type === t).value;
  /* Dubai is UTC+4 the year round - no daylight saving - so midnight there
     is 20:00 UTC on the day before. */
  return Date.parse(`${get('year')}-${get('month')}-${get('day')}T00:00:00+04:00`);
}

/** Today's midnight, Dubai. */
export const dubaiToday = () => dubaiDayStart(Date.now());

/** "Today" / "Tomorrow" / "Yesterday" / "Thursday", Dubai's days. */
export function dayLabel(d, today = dubaiToday()) {
  const diff = Math.round((dubaiDayStart(d) - today) / DAY);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return weekday(d);
}

/* ----------------------------------------------------------- the periods

   A report is asked for in whole days, weeks, months and years - Dubai's,
   like everything else here. A day is therefore a string, 'YYYY-MM-DD', and
   the arithmetic is done on the calendar rather than on milliseconds: adding
   a month to 31 January must land in February, which epoch maths does not do.

   Every range is [from, to) - the start included, the end not - so two
   consecutive periods neither overlap nor leave a gap at midnight.          */

/** The Dubai day an instant falls on, as 'YYYY-MM-DD'. */
export const ymd = d => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date(d));

/** Midnight Dubai on that day, as epoch ms. */
export const atDubai = day => Date.parse(`${day}T00:00:00+04:00`);

const partsOf = day => day.split('-').map(Number);
const asDay = t => t.toISOString().slice(0, 10);

/** n days on from that day; n may be negative. */
export function addDays(day, n) {
  const [y, m, d] = partsOf(day);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + n);
  return asDay(t);
}

/** n months on from the first of that day's month. */
export function addMonths(day, n) {
  const [y, m] = partsOf(day);
  return asDay(new Date(Date.UTC(y, m - 1 + n, 1)));
}

/** The Monday of that day's week. The working week starts on Monday here. */
export function weekStart(day) {
  const [y, m, d] = partsOf(day);
  const dow = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
  return addDays(day, -dow);
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const dayName = day => dayText(`${day}T12:00:00+04:00`);
const dayShortName = day => dayName(day).replace(/ \d{4}$/, '');

/**
 * One reporting period: which days it covers, and what to call it.
 *
 * `offset` steps back and forward in whole periods, which is how "yesterday"
 * is asked for - the daily report, one step back - and how last month is
 * reached without typing dates.
 */
export function periodRange(grain, offset = 0, custom = {}) {
  const today = ymd(Date.now());

  if (grain === 'custom') {
    const from = custom.from || today;
    const to = custom.to || from;
    const lo = from <= to ? from : to;
    const hi = from <= to ? to : from;
    const days = Math.round((atDubai(hi) - atDubai(lo)) / DAY) + 1;
    return {
      grain, from: lo, to: addDays(hi, 1), days,
      label: lo === hi ? dayName(lo) : `${dayShortName(lo)} – ${dayName(hi)}`,
      sub: `${days} day${days === 1 ? '' : 's'}`
    };
  }

  if (grain === 'week') {
    const from = addDays(weekStart(today), offset * 7);
    const last = addDays(from, 6);
    return {
      grain, from, to: addDays(from, 7), days: 7,
      label: `${dayShortName(from)} – ${dayName(last)}`,
      sub: offset === 0 ? 'This week' : offset === -1 ? 'Last week' : `${Math.abs(offset)} weeks ${offset < 0 ? 'back' : 'ahead'}`
    };
  }

  if (grain === 'month') {
    const from = addMonths(today, offset);
    const to = addMonths(from, 1);
    const [y, m] = partsOf(from);
    return {
      grain, from, to, days: Math.round((atDubai(to) - atDubai(from)) / DAY),
      label: `${MONTHS[m - 1]} ${y}`,
      sub: offset === 0 ? 'This month' : offset === -1 ? 'Last month' : `${Math.abs(offset)} months ${offset < 0 ? 'back' : 'ahead'}`
    };
  }

  if (grain === 'year') {
    const [y] = partsOf(today);
    const from = `${y + offset}-01-01`;
    const to = `${y + offset + 1}-01-01`;
    return {
      grain, from, to, days: Math.round((atDubai(to) - atDubai(from)) / DAY),
      label: String(y + offset),
      sub: offset === 0 ? 'This year' : offset === -1 ? 'Last year' : `${Math.abs(offset)} years ${offset < 0 ? 'back' : 'ahead'}`
    };
  }

  const from = addDays(today, offset);
  return {
    grain: 'day', from, to: addDays(from, 1), days: 1,
    label: dayName(from),
    sub: offset === 0 ? 'Today' : offset === -1 ? 'Yesterday' : weekday(`${from}T12:00:00+04:00`)
  };
}

/**
 * The run of periods ending at this one, for the trend.
 *
 * Reading a single day's bookings as a chart of its own hours says nothing;
 * reading it against the fortnight around it says whether it was a good day.
 */
export function trendBuckets(p, custom = {}) {
  if (p.grain === 'custom') {
    const n = Math.min(31, Math.max(1, p.days));
    const step = Math.ceil(p.days / n);
    const out = [];
    for (let d = p.from; d < p.to; d = addDays(d, step)) {
      const to = addDays(d, step);
      out.push({ key: d, from: d, to: to > p.to ? p.to : to, label: dayShortName(d) });
    }
    return { buckets: out, title: `Demos across ${dayShortName(p.from)} – ${dayShortName(addDays(p.to, -1))}` };
  }

  const n = p.grain === 'day' ? 14 : p.grain === 'week' ? 12 : p.grain === 'month' ? 12 : 5;
  const back = i => {
    if (p.grain === 'day') return addDays(p.from, -i);
    if (p.grain === 'week') return addDays(p.from, -i * 7);
    if (p.grain === 'month') return addMonths(p.from, -i);
    const [y] = partsOf(p.from);
    return `${y - i}-01-01`;
  };
  const next = d => (p.grain === 'day' ? addDays(d, 1)
    : p.grain === 'week' ? addDays(d, 7)
      : p.grain === 'month' ? addMonths(d, 1)
        : `${partsOf(d)[0] + 1}-01-01`);
  const name = d => {
    if (p.grain === 'day') return dayShortName(d);
    if (p.grain === 'week') return dayShortName(d);
    if (p.grain === 'month') return `${MONTHS[partsOf(d)[1] - 1].slice(0, 3)} ${String(partsOf(d)[0]).slice(2)}`;
    return d.slice(0, 4);
  };
  const buckets = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const from = back(i);
    buckets.push({ key: from, from, to: next(from), label: name(from), now: i === 0 });
  }
  const unit = p.grain === 'day' ? `${n} days` : p.grain === 'week' ? `${n} weeks` : p.grain === 'month' ? `${n} months` : `${n} years`;
  return { buckets, title: `Demos over the last ${unit}` };
}

/* ------------------------------------------------------------- the state */

/** What state the booking is in now, from the clock and Calendly's flags. */
export function stateOf(b, now = Date.now()) {
  if (b.status === 'canceled') return 'canceled';
  if (b.noShow || (b.session && b.session.attendance === 'no_show')) return 'noshow';
  const end = new Date(b.end).getTime();
  const start = new Date(b.start).getTime();
  if (end < now) return 'done';
  if (start <= now) return 'live';
  return 'upcoming';
}

export const STATE_META = {
  upcoming: ['Upcoming', 'gr-blue', 'clock'],
  live: ['Happening now', 'gr-green', 'zap'],
  done: ['Completed', 'gr-slate', 'check'],
  noshow: ['No-show', 'gr-amber', 'eyeoff'],
  canceled: ['Cancelled', 'gr-red', 'x']
};

/**
 * A B2B partner runs a course of sessions rather than one demo, so each
 * session is closed with feedback and only escalates to sales when somebody
 * asks for it. A B2C demo hands over to sales as a matter of course.
 */
export const isPartner = b => !!(b.session && b.session.customerType === 'B2B');

/**
 * Whether this booking belongs to a client of ours.
 *
 *   matched  the email or phone found the account
 *   manual   somebody who knows said so, because the address they booked
 *            with is not the one they pay under
 *   none     neither
 *
 * A contact on the CRM is not registration: they are somebody we have a
 * card for, which is not the same as somebody with an account.
 */
export const REGISTERED = b => (b.client ? 'matched'
  : b.session && b.session.manualRegistered ? 'manual' : 'none');

export const REG_META = {
  matched: ['Registered', 'gr-teal', 'check'],
  manual: ['Registered manually', 'gr-violet', 'check'],
  none: ['Not registered', 'gr-amber', '']
};

/**
 * The words on the badge: registered, and on what plan.
 *
 * One function because there are three badges — the booking row, the
 * follow-up row and the panel that opens under it — and they must agree.
 * Written out three times they did not: the row said "Registered" where the
 * panel two lines below it said "Registered · FREE", which reads as two
 * different answers to one question.
 *
 * A matched client carries the plan the accounts API knows; one registered
 * by hand carries the plan whoever did it chose. Either way it goes on the
 * badge, because "registered" without it invites the next question.
 */
export function regLabel(b) {
  const how = REGISTERED(b);
  const [label] = REG_META[how];
  const plan = how === 'matched' ? (b.client && b.client.plan)
    : how === 'manual' ? (b.session && b.session.manualPlan)
      : '';
  return plan ? `${label} · ${plan}` : label;
}

/** Whether the person is on the books, and under which id. */
export function whoOf(b) {
  if (b.client) return { kind: 'client', name: b.client.name, code: b.client.code, plan: b.client.plan, registered: true };
  if (b.contact) return { kind: 'prospect', name: b.contact.name, code: b.contact.code, registered: false };
  return { kind: 'none', name: '', code: '', registered: false };
}

/**
 * Whether the booking has reached the point where sales takes over: it
 * happened, or they did not turn up. Everything else is Calendly's business.
 */
export const isWorkable = (b, now = Date.now()) => {
  const st = stateOf(b, now);
  return st === 'done' || st === 'noshow';
};

/**
 * Where the follow-up has got to.
 *
 *   open      the demo happened and nobody has handed it on — it is still
 *             the demo team's, and only the bookings screen shows it
 *   handed    the demo team passed it to sales, who have not started
 *   following sales picked it up and is chasing
 *   closed    finished with, for a reason (converted and rebooked end it too)
 */
export const followState = b => {
  const v = (b.session || {}).followStatus || 'open';
  if (v === 'following') return 'following';
  if (v === 'handed') return 'handed';
  if (v === 'open') return 'open';
  return 'closed';
};

/**
 * What one round of chasing came to.
 *
 * Every note carries one of these, so the trail reads as a journey rather
 * than as a pile of paragraphs, and the closed list can be filtered by how
 * it ended. The last two end the chase.
 */
export const STEP_META = {
  no_response:     ['No response', 'gr-slate', 'eyeoff'],
  /* Reached out and it failed — the line was busy, the number was wrong.
     Not the same as hearing nothing back, and the team asked for both. */
  couldnt_connect: ['Couldn\'t connect', 'gr-amber', 'phone'],
  /* A thing somebody did, so it is a round. Written as a note under "Follow
     up" it made the trail read as though they had spoken. */
  mail_sent:       ['Mail sent', 'gr-cyan', 'mail'],
  call_back:       ['Call back', 'gr-blue', 'clock'],
  follow_up:       ['Follow up', 'gr-violet', 'flag'],
  /* Booked another one. It ends this chase — the new booking carries its
     own. */
  demo_rescheduled: ['Demo rescheduled', 'gr-indigo', 'cal'],
  not_interested:  ['Not interested', 'gr-red', 'x'],
  success:         ['Success', 'gr-green', 'check']
};

/** How a closed follow-up ended: [label, tone, icon]. */
export const CLOSED_META = {
  demo_rescheduled: ['Demo rescheduled', 'gr-indigo', 'cal'],
  not_interested: ['Not interested', 'gr-red', 'x'],
  plan_upgrade:   ['Plan upgradation', 'gr-green', 'up'],
  registration:   ['Registration', 'gr-teal', 'check'],
  other:          ['Closed', 'gr-slate', 'check']
};

/** The last note written on a follow-up, or null. */
export const lastStep = b => {
  const list = (b.session && b.session.feedback) || [];
  return list.length ? list[list.length - 1] : null;
};

/**
 * A round that ends the chase and is still waiting to be closed.
 *
 * Derived from the trail rather than remembered, because the wait has to
 * survive the panel being folded away, the list reloading and the page
 * being opened again tomorrow. Held in component state it survived none of
 * them: writing "not interested" and collapsing the row took the Close
 * button with it, leaving a follow-up nobody could finish and nobody could
 * carry on either.
 *
 * Returns what Close would close it as, and the words to close it with.
 */
export function pendingClose(b) {
  if (followState(b) === 'closed') return null;
  const last = lastStep(b);
  if (!last) return null;
  /* Reopening leaves the ending round where it is. Somebody who has just
     decided to carry on should not be met by a button asking them to close
     it again, so anything written before the reopen is history. */
  const re = b.session && b.session.reopenedAt;
  if (re && new Date(last.at) <= new Date(re)) return null;
  if (last.kind === 'demo_rescheduled') return { as: 'demo_rescheduled', why: last.text || '' };
  if (last.kind === 'not_interested') return { as: 'not_interested', why: last.text || '' };
  if (last.kind === 'success') return { as: last.plan || 'plan_upgrade', why: last.text || '' };
  return null;
}

/** Badge for each follow-up state: [label, tone, icon]. */
export const FOLLOW_META = {
  handed: ['Not started', 'gr-amber', 'alert'],
  following: ['Following up', 'gr-blue', 'flag'],
  closed: ['Closed', 'gr-slate', 'check']
};

/** When the demo team handed it over, as ms — newest hand-overs lead. */
const handedAt = b => {
  const s = b.session || {};
  return s.handedAt ? new Date(s.handedAt).getTime() : new Date(b.start).getTime();
};

/**
 * Whether a booking is waiting on somebody, and what for.
 *
 * Asked for the last two weeks only: what happened in May is a backlog to
 * work through by filter, not two hundred red rows.
 */
export function needsAction(b, now = Date.now()) {
  const s = b.session;
  if (!s) return '';
  const state = stateOf(b, now);
  const fs = followState(b);
  const recent = now - new Date(b.end).getTime() < 14 * DAY;

  if (fs === 'closed') return '';
  /* Handed over: it is sales' job now, and the booking row says so with a
     badge. Flagging it as waiting on somebody here would paint the demo
     team's own diary red for work that has left them. */
  if (fs === 'handed') return '';
  if (fs === 'open') {
    /* Nobody has picked it up yet. */
    if (state === 'noshow') return recent ? 'Start the follow-up' : '';
    if (state === 'done') return recent ? 'Start the follow-up' : '';
    if (state === 'canceled' && b.cancellation && b.cancellation.by === 'invitee'
        && now - new Date(b.cancellation.at || b.start).getTime() < 7 * DAY) {
      return 'Follow up or close';
    }
    return '';
  }
  /* Being followed up: only the clock can make it urgent. */
  if (s.nextDue && Date.parse(`${s.nextDue}T23:59:59+04:00`) < now) {
    return `Follow-up overdue: ${s.nextAction || 'no action set'}`;
  }
  return '';
}

/**
 * The sales worklist: the demos the demo team has actually handed over.
 *
 * Nothing arrives here on its own. A demo that happened is the demo team's
 * until somebody presses Follow up on the booking, and that is deliberate -
 * the list used to carry every past demo, which made it a second copy of
 * the diary rather than a queue of work.
 *
 * What has just been handed over leads, newest first, so the row somebody
 * sent a minute ago is the first thing sales sees.
 */
export function followUps(demos) {
  return (demos?.bookings || [])
    .filter(b => b.session && followState(b) !== 'open')
    .sort((a, b2) => {
      const rank = x => ({ handed: 0, following: 1, closed: 2 }[followState(x)]);
      if (rank(a) !== rank(b2)) return rank(a) - rank(b2);
      if (rank(a) === 0) return handedAt(b2) - handedAt(a);
      if (rank(a) === 1) {
        const due = x => (x.session.nextDue ? Date.parse(`${x.session.nextDue}T00:00:00+04:00`) : Infinity);
        if (due(a) !== due(b2)) return due(a) - due(b2);
      }
      return new Date(b2.start) - new Date(a.start);
    });
}

/**
 * Every earlier demo the same person had, newest first.
 *
 * Matched the way the bookings themselves are - by client, then contact,
 * then email - so a partner's fourth session can be read with the notes and
 * the feedback from the first three beside it.
 */
export function earlierDemos(b, demos) {
  return sameWho(b, demos)
    .filter(x => new Date(x.start) < new Date(b.start))
    .sort((x, y) => new Date(y.start) - new Date(x.start));
}

/**
 * The demos this person booked after this one, soonest first.
 *
 * The other half of "Demo 3 of 5": the badge counted the fourth and fifth
 * and nothing on the row showed them. A no-show who came back the next day
 * under the company's name reads, without this, as somebody still to chase.
 */
export function laterDemos(b, demos) {
  return sameWho(b, demos)
    .filter(x => new Date(x.start) > new Date(b.start))
    .sort((x, y) => new Date(x.start) - new Date(y.start));
}

/* Same person, by the key the server counts demos with. */
function sameWho(b, demos) {
  const s = b.session;
  if (!s || !demos || !demos.bookings) return [];
  const key = x => {
    const t = x.session || {};
    return String(t.clientId || t.contactId || String(x.invitee.email || '').toLowerCase() || t.id);
  };
  const mine = key(b);
  return demos.bookings.filter(x => x.session && x.id !== b.id && key(x) === mine);
}

/** "3 days ago", "in 2 hours", "just now". */
export function ago(iso, now = Date.now()) {
  const t = new Date(iso).getTime();
  if (!t) return '';
  const s = Math.round((now - t) / 1000);
  const abs = Math.abs(s);
  const unit = abs < 60 ? null
    : abs < 3600 ? [Math.round(abs / 60), 'minute']
      : abs < 86400 ? [Math.round(abs / 3600), 'hour']
        : abs < 30 * 86400 ? [Math.round(abs / 86400), 'day']
          : [Math.round(abs / (30 * 86400)), 'month'];
  if (!unit) return 'just now';
  const text = `${unit[0]} ${unit[1]}${unit[0] === 1 ? '' : 's'}`;
  return s >= 0 ? `${text} ago` : `in ${text}`;
}
